import React, { useState, useEffect } from 'react';
import { Search, Download, MessageCircle, X, ChevronDown, Plus, Edit2, Trash2, AlertTriangle, Edit } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function JefaturaDocentesDocs() {
    const [activeTab, setActiveTab] = useState('solicitados'); // 'solicitados' | 'entregados'
    const [requisitos, setRequisitos] = useState([]);
    const [documentos, setDocumentos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [anio, setAnio] = useState(new Date().getFullYear().toString());

    // Modal estado para evaluación
    const [showModal, setShowModal] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [puntaje, setPuntaje] = useState('');
    const [comentario, setComentario] = useState('');

    // Modal estado para requisitos
    const [showRequisitoModal, setShowRequisitoModal] = useState(false);
    const [editingRequisito, setEditingRequisito] = useState(null);
    const [requisitoForm, setRequisitoForm] = useState({
        tipo_documento: '',
        descripcion: '',
        fecha_limite: ''
    });

    useEffect(() => {
        initTables();
    }, []);

    useEffect(() => {
        if (activeTab === 'solicitados') {
            loadRequisitos();
        } else {
            loadDocumentacion();
        }
    }, [anio, activeTab]);

    const initTables = async () => {
        try {
            await fetch(`${API_URL}/jefatura/docentes/requisitos/init`);
        } catch (error) {
            console.log('Tables already exist or init failed');
        }
    };

    const loadRequisitos = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/jefatura/docentes/requisitos?anio=${anio}`);
            const data = await response.json();
            setRequisitos(data.requisitos || []);
        } catch (error) {
            console.error('Error:', error);
            setRequisitos([]);
        } finally {
            setLoading(false);
        }
    };

    const loadDocumentacion = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/jefatura/docentes/documentacion?anio=${anio}`);
            const data = await response.json();
            setDocumentos(data.documentacion || []);
        } catch (error) {
            console.error('Error:', error);
            setDocumentos([]);
        } finally {
            setLoading(false);
        }
    };

    // ============ REQUISITOS ============
    const handleOpenRequisitoModal = (requisito = null) => {
        if (requisito) {
            setEditingRequisito(requisito);

            // Fix timezone issue: parse the date correctly for datetime-local input
            let fechaLimiteFormatted = '';
            if (requisito.fecha_limite) {
                // Create date object and format in local timezone
                const fecha = new Date(requisito.fecha_limite);
                // Format as YYYY-MM-DDTHH:MM for datetime-local input
                const year = fecha.getFullYear();
                const month = String(fecha.getMonth() + 1).padStart(2, '0');
                const day = String(fecha.getDate()).padStart(2, '0');
                const hours = String(fecha.getHours()).padStart(2, '0');
                const minutes = String(fecha.getMinutes()).padStart(2, '0');
                fechaLimiteFormatted = `${year}-${month}-${day}T${hours}:${minutes}`;
            }

            setRequisitoForm({
                tipo_documento: requisito.tipo_documento || '',
                descripcion: requisito.descripcion || '',
                fecha_limite: fechaLimiteFormatted
            });
        } else {
            setEditingRequisito(null);
            setRequisitoForm({ tipo_documento: '', descripcion: '', fecha_limite: '' });
        }
        setShowRequisitoModal(true);
    };

    const handleSaveRequisito = async () => {
        if (!requisitoForm.tipo_documento || !requisitoForm.fecha_limite) {
            alert('Documento Académico y Fecha Límite son requeridos');
            return;
        }

        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const url = editingRequisito
                ? `${API_URL}/jefatura/docentes/requisitos/${editingRequisito.id_requisito}`
                : `${API_URL}/jefatura/docentes/requisitos`;

            await fetch(url, {
                method: editingRequisito ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...requisitoForm,
                    created_by: user.id_usuario
                })
            });

            setShowRequisitoModal(false);
            loadRequisitos();
        } catch (error) {
            console.error('Error:', error);
            alert('Error al guardar requisito');
        }
    };

    const handleDeleteRequisito = async (id) => {
        if (!window.confirm('¿Eliminar este requisito?')) return;

        try {
            const response = await fetch(`${API_URL}/jefatura/docentes/requisitos/${id}`, {
                method: 'DELETE'
            });
            const data = await response.json();

            if (!response.ok) {
                alert(data.error || 'Error al eliminar');
                return;
            }

            loadRequisitos();
        } catch (error) {
            console.error('Error:', error);
        }
    };

    // ============ DOCUMENTOS ENTREGADOS ============
    const handleOpenModal = (doc) => {
        setSelectedDoc(doc);
        setPuntaje(doc.puntaje_jefatura || '');
        setComentario(doc.comentarios_jefatura || '');
        setShowModal(true);
    };

    const handleEnviar = async () => {
        if (!selectedDoc) return;
        try {
            await fetch(`${API_URL}/jefatura/docentes/documentacion/${selectedDoc.id_documento}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    puntaje_jefatura: puntaje ? parseFloat(puntaje) : null,
                    comentarios_jefatura: comentario,
                    estado: 'enviado'
                })
            });
            setShowModal(false);
            setSelectedDoc(null);
            setPuntaje('');
            setComentario('');
            loadDocumentacion();
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const getEstadoLabel = (estado) => {
        switch (estado) {
            case 'pendiente': return 'Pendiente';
            case 'enviado': return 'Enviado';
            case 'enviado con retraso': return 'Enviado con retraso';
            case 'revisado': return 'Revisado';
            case 'publicado': return 'Publicado';
            case 'vencido': return 'Vencido';
            default: return estado || 'Pendiente';
        }
    };

    const getEstadoColor = (estado) => {
        switch (estado) {
            case 'pendiente': return 'bg-gray-200 text-gray-700';
            case 'enviado': return 'bg-blue-200 text-blue-700';
            case 'enviado con retraso': return 'bg-red-200 text-red-700 font-bold';
            case 'revisado': return 'bg-green-200 text-green-700';
            case 'publicado': return 'bg-green-100 text-green-700';
            case 'vencido': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-200 text-gray-700';
        }
    };

    const filteredRequisitos = requisitos.filter(r =>
        searchTerm === '' ||
        r.tipo_documento?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredDocs = documentos.filter(d =>
        searchTerm === '' ||
        `${d.nombre_docente || ''} ${d.nombres || ''} ${d.apellidos || ''}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.tipo_documento?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.carrera?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const anios = Array.from({ length: 10 }, (_, i) => (2020 + i).toString());

    if (loading) return <div className="p-6 text-center">Cargando...</div>;

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">Jefatura de Carrera &gt; Docentes &gt; Documentación</h1>
            </div>

            {/* Tabs */}
            <div className="mb-4 flex gap-2">
                <button
                    onClick={() => setActiveTab('solicitados')}
                    className={`px-6 py-2.5 rounded-full font-medium transition ${activeTab === 'solicitados'
                        ? 'bg-blue-900 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                    Documentos Solicitados
                </button>
                <button
                    onClick={() => setActiveTab('entregados')}
                    className={`px-6 py-2.5 rounded-full font-medium transition ${activeTab === 'entregados'
                        ? 'bg-blue-900 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                    Documentos Entregados
                </button>
            </div>

            {/* Filtros */}
            {/* <div className="mb-4 flex flex-wrap gap-3 items-center">
                <div className="relative">
                    <select value={anio} onChange={(e) => setAnio(e.target.value)}
                        className="appearance-none px-4 py-2 pr-10 border border-gray-300 rounded-full bg-white">
                        <option value="">Año</option>
                        {anios.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>
                <div className="flex-1 max-w-md relative">
                    <input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-full" />
                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                </div>

                {activeTab === 'solicitados' && (
                    <button
                        onClick={() => handleOpenRequisitoModal()}
                        className="flex items-center gap-2 px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800"
                    >
                        <Plus size={18} />
                        Documento
                    </button>
                )}
            </div> */}

            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-wrap gap-4 items-end">

                    <div className="relative">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">GESTIÓN</label>
                        <select
                            value={anio} onChange={(e) => setAnio(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                        >
                            {anios.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                        <span className="absolute right-3 top-[30px] pointer-events-none text-gray-400 text-xs">▼</span>
                    </div>
                    <div className="flex-grow flex items-end gap-2">
                        <div className="relative flex-grow">
                            <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Buscar..."
                                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                />
                            </div>
                        </div>
                        {activeTab === 'solicitados' && (
                            <button
                                onClick={() => handleOpenRequisitoModal()}
                                className="flex items-center gap-2 px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800"
                            >
                                <Plus size={18} />
                                Documento
                            </button>
                        )}

                    </div>
                </div>
            </div>
            {/* Content based on active tab */}
            {activeTab === 'solicitados' ? (
                /* TABLA REQUISITOS/SOLICITADOS */
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-3 py-3 text-center font-semibold border">No</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Documento Académico</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Descripción</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Fecha y Hora Publicación</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Fecha y Hora Presentación</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {filteredRequisitos.length === 0 ? (
                                    <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">No hay requisitos registrados</td></tr>
                                ) : (
                                    filteredRequisitos.map((r, index) => (
                                        <tr key={r.id_requisito} className="hover:bg-gray-50">
                                            <td className="px-3 py-2 border text-center">{index + 1}</td>
                                            <td className="px-3 py-2 border font-medium">{r.tipo_documento}</td>
                                            <td className="px-3 py-2 border">{r.descripcion || '-'}</td>
                                            <td className="px-3 py-2 border text-center">
                                                {r.fecha_publicacion ? new Date(r.fecha_publicacion).toLocaleString('es-BO', {
                                                    day: '2-digit', month: '2-digit', year: 'numeric',
                                                    hour: '2-digit', minute: '2-digit'
                                                }) : '-'}
                                            </td>
                                            <td className="px-3 py-2 border text-center">
                                                {r.fecha_limite ? new Date(r.fecha_limite).toLocaleString('es-BO', {
                                                    day: '2-digit', month: '2-digit', year: 'numeric',
                                                    hour: '2-digit', minute: '2-digit'
                                                }) : '-'}
                                            </td>
                                            <td className="px-3 py-2 text-center border">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(r.estado)}`}>
                                                    {getEstadoLabel(r.estado)}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-center border">
                                                <div className="flex justify-center gap-1">
                                                    <button
                                                        onClick={() => handleOpenRequisitoModal(r)}
                                                        className="p-2 rounded-full transition shadow-sm bg-blue-100 text-blue-600 hover:bg-blue-200"
                                                        title="Editar"
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteRequisito(r.id_requisito)}
                                                        className="p-2 rounded-full transition shadow-sm bg-red-100 text-red-600 hover:bg-red-200"
                                                        title="Eliminar"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                /* TABLA DOCUMENTOS ENTREGADOS */
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th className="px-3 py-3 text-center font-semibold border">No</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Docente</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Documento Académico</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Carrera</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Mención</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Asignatura</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Paralelo</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Fecha Presentación</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Puntaje</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Comentarios</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {filteredDocs.length === 0 ? (
                                    <tr><td colSpan="12" className="px-3 py-2 text-center border">No hay documentación registrada</td></tr>
                                ) : (
                                    filteredDocs.map((d, index) => (
                                        <tr key={d.id_documento} className="hover:bg-gray-50">
                                            <td className="px-3 py-2 border text-center">{index + 1}</td>
                                            <td className="px-3 py-2 border">{d.nombre_docente || `${d.nombres || ''} ${d.apellidos || ''}`.trim() || '-'}</td>
                                            <td className="px-3 py-2 border">{d.tipo_documento || d.requisito_tipo || '-'}</td>
                                            <td className="px-3 py-2 border">{d.carrera || '-'}</td>
                                            <td className="px-3 py-2 border">{d.mencion || '-'}</td>
                                            <td className="px-3 py-2 border">{d.asignatura || '-'}</td>
                                            <td className="px-3 py-2 border text-center">{d.paralelo || '-'}</td>
                                            <td className={`px-3 py-2 border text-center ${d.entrega_tardia ? 'text-red-700 font-bold' : ''}`}>
                                                {d.fecha_presentacion
                                                    ? new Date(d.fecha_presentacion).toLocaleString('es-BO', {
                                                        day: '2-digit', month: '2-digit', year: 'numeric',
                                                        hour: '2-digit', minute: '2-digit'
                                                    })
                                                    : d.created_at
                                                        ? new Date(d.created_at).toLocaleString('es-BO', {
                                                            day: '2-digit', month: '2-digit', year: 'numeric',
                                                            hour: '2-digit', minute: '2-digit'
                                                        })
                                                        : '-'}
                                            </td>
                                            <td className="px-3 py-2 border">
                                                <div className="flex justify-center gap-1">
                                                    <button
                                                        onClick={() => {
                                                            if (d.archivo_url) {
                                                                const token = localStorage.getItem('token');
                                                                const url = `${API_URL}/docente/documentos/download/${d.id_documento}?token=${token}`;

                                                                // Crear un enlace temporal
                                                                const link = document.createElement('a');
                                                                link.href = url;
                                                                // No usamos '_blank', se descargará en la misma página
                                                                link.setAttribute('download', ''); // Fuerza la descarga
                                                                document.body.appendChild(link);
                                                                link.click();
                                                                document.body.removeChild(link); // Limpiar
                                                            } else {
                                                                alert('No hay archivo adjunto');
                                                            }
                                                        }}
                                                        className="p-2 rounded-full bg-green-600 text-white hover:bg-green-700 transition shadow-sm"
                                                        title="Descargar documento"
                                                        disabled={!d.archivo_url}
                                                    >
                                                        <Download size={20} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenModal(d)}
                                                        className="p-2 rounded-full bg-blue-900 text-white hover:bg-blue-800 transition-colors"
                                                        title="Evaluar"
                                                    >
                                                        <MessageCircle size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 border text-center">
                                                {d.puntaje_jefatura ? `${d.puntaje_jefatura}%` : '-'}
                                            </td>
                                            <td className="px-3 py-2 border">{d.comentarios_jefatura || '-'}</td>
                                            <td className="px-3 py-2 border text-center">
                                                <span className={`px-2 py-1 rounded-full text-xs whitespace-nowrap ${(d.entrega_tardia || d.estado === 'enviado con retraso')
                                                    ? 'bg-red-200 text-red-800 font-bold'
                                                    : getEstadoColor(d.estado)
                                                    }`}>
                                                    {(d.entrega_tardia || d.estado === 'enviado con retraso')
                                                        ? 'Enviado con retraso'
                                                        : getEstadoLabel(d.estado)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal de Evaluación */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <h2 className="text-xl font-bold text-blue-900">Evaluación</h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="mb-4">
                                <label className="block text-sm text-gray-700 mb-2">Puntaje (%)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={puntaje}
                                    onChange={(e) => setPuntaje(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="0 - 100"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm text-gray-700 mb-2">Comentarios</label>
                                <textarea
                                    value={comentario}
                                    onChange={(e) => setComentario(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    rows={4}
                                    placeholder="Escriba sus comentarios..."
                                />
                            </div>
                        </div>
                        <div className="flex justify-end px-6 py-4 border-t">
                            <button
                                onClick={handleEnviar}
                                className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
                            >
                                Enviar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Requisito */}
            {showRequisitoModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="flex justify-between items-center px-6 py-4 border-b sticky top-0 bg-white z-10">
                            <h2 className="text-xl font-bold text-blue-900">{editingRequisito ? 'Editar Documento' : 'Nuevo Documento'}</h2>
                            <button onClick={() => setShowRequisitoModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Documento Académico <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={requisitoForm.tipo_documento}
                                    onChange={(e) => setRequisitoForm({ ...requisitoForm, tipo_documento: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Ej: Plan Académico, Syllabus, etc."
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                                <textarea
                                    value={requisitoForm.descripcion}
                                    onChange={(e) => setRequisitoForm({ ...requisitoForm, descripcion: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    rows={3}
                                    placeholder="Descripción del documento requerido..."
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Fecha y Hora de Presentación <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="datetime-local"
                                    value={requisitoForm.fecha_limite}
                                    onChange={(e) => setRequisitoForm({ ...requisitoForm, fecha_limite: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 px-6 py-4 border-t rounded-b-lg">
                            <button
                                onClick={() => setShowRequisitoModal(false)}
                                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveRequisito}
                                className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
                            >
                                {editingRequisito ? 'Actualizar' : 'Guardar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
